export interface UsageByApi {
  api: string;
  sku: string;
  requests: number;
  costUsd: number;
}

export interface UsageDay {
  /** The UTC calendar day this bar covers, `YYYY-MM-DD`. Absent on a series
   *  that is not calendar days (the request histogram buckets by minutes) and
   *  on a gateway that predates the field; a bar without it does not drill in. */
  date?: string;
  label: string;
  requests: number;
  costUsd: number;
}

export interface AccountUsage {
  periodLabel: string;
  /** The IANA zone the gateway bucketed `daily` into - the one we asked for,
   *  or `UTC` when it could not load ours. A drill-down window built in any
   *  other zone would not match these bars. */
  timeZone: string;
  totalRequests: number;
  totalCostUsd: number;
  avgCostPerRequest: number;
  byApi: UsageByApi[];
  daily: UsageDay[];
}

/** The windows a usage report can cover. `3m`, `6m` and `1y` are calendar
 *  periods ending today in the report's zone; `all` starts at the first request. */
export const REPORT_PERIODS = ['7d', '14d', '30d', '3m', '6m', '1y', 'all'] as const;

export type ReportPeriod = (typeof REPORT_PERIODS)[number];

export interface ReportSkuSpend {
  sku: string;
  api: string;
  requests: number;
  costUsd: number;
}

export interface ReportKeySpend {
  /** `null` gathers the requests made without an API key (dashboard, OAuth). */
  keyId: string | null;
  name: string;
  requests: number;
  costUsd: number;
}

export interface ReportDay {
  /** A calendar day in the report's zone, `YYYY-MM-DD`. */
  date: string;
  requests: number;
  costUsd: number;
}

export interface ReportRow extends ReportDay {
  sku: string;
  api: string;
}

/** One account's charged usage over a period, as the gateway totals it. Every
 *  money total is the gateway's; nothing here is summed on the client. */
export interface UsageReport {
  period: ReportPeriod;
  timeZone: string;
  /** Inclusive calendar days in `timeZone`, `YYYY-MM-DD`. */
  from: string;
  to: string;
  /** The instant the gateway computed the report (RFC 3339). */
  asOf: string;
  totalRequests: number;
  totalCostUsd: number;
  avgCostPer1kUsd: number;
  /** Ranked by spend, highest first. */
  bySku: ReportSkuSpend[];
  /** Ranked by spend, highest first. */
  byKey: ReportKeySpend[];
  /** Every day from `from` to `to`, zero-filled. */
  daily: ReportDay[];
  /** One per (day, SKU) with usage: date ascending, then spend descending. */
  rows: ReportRow[];
}
